import { Controller } from "@hotwired/stimulus";

// Modal dialog over a native <dialog>: a trigger opens it, Escape and the
// backdrop close it, and the page behind keeps still while it is open. The
// dialog element is the panel — the browser centres it and draws the backdrop,
// so nothing has to be positioned by hand.
//
//   <div data-controller="modal">
//     <button data-action="modal#open">Kampanyayı gör</button>
//     <dialog data-modal-target="dialog" class="…open:flex …"
//             data-action="close->modal#released click->modal#dismissOnClickOutside">
//       …
//       <button data-action="modal#close">Kapat</button>
//     </dialog>
//   </div>
export default class extends Controller {
  static targets = ["dialog"];
  // The id of a dialog to open as soon as the element arrives — set by markup
  // a Turbo Stream draws in answer to something the reader just did (a "Hayır"
  // at the foot of a detail page opens the report form straight away).
  static values = { autoOpen: String };

  connect() {
    if (this.autoOpenValue) this.open({ params: { dialog: this.autoOpenValue } });
  }

  // A trigger outside the dialog's own scope names it instead, so a second way
  // into the same dialog does not need a second copy of it:
  //
  //   <div data-controller="modal">
  //     <button data-action="modal#open" data-modal-dialog-param="sorun-bildir">…</button>
  //   </div>
  open({ params: { dialog } = {} }) {
    (dialog ? document.getElementById(dialog) : this.dialogTarget).showModal();
    // The page behind the dialog keeps its scrollbar otherwise.
    document.body.classList.add("overflow-hidden");
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close();
    // Released here as well as on the event: closing from a control inside the
    // dialog does not always produce a `close` event, and a lock that is never
    // lifted leaves the page unable to scroll.
    this.released();
  }

  // Wired to the dialog's `close` event, which is what covers Escape and any
  // other close the browser performs on its own. Removing a class twice is
  // harmless, so the two paths do not need to know about each other.
  released() {
    document.body.classList.remove("overflow-hidden");
  }

  // A dialog fills the viewport for hit-testing: a click that lands on the
  // element itself rather than on anything inside it is a click on the backdrop.
  dismissOnClickOutside(event) {
    if (event.target === this.dialogTarget) this.close();
  }

  // Guarded: a scope that never rendered its dialog must still tear down cleanly.
  disconnect() {
    if (this.hasDialogTarget) this.close();
    this.released();
  }
}
